import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import useSiteInfo, { hoursLines, telHref } from "@/hooks/useSiteInfo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  CheckCircle,
  Send,
  Car,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const site = useSiteInfo();
  const location = useLocation();
  const vehicleFromState = location.state?.vehicle || null;
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: vehicleFromState ? "Fahrzeuganfrage" : "",
    message: vehicleFromState 
      ? `Ich interessiere mich für das Fahrzeug: ${vehicleFromState.brand} ${vehicleFromState.model} (${vehicleFromState.year})\n\nBitte kontaktieren Sie mich für weitere Informationen.`
      : "",
    // Vehicle data
    vehicle_id: vehicleFromState?.id || "",
    vehicle_brand: vehicleFromState?.brand || "",
    vehicle_model: vehicleFromState?.model || "",
    vehicle_price: vehicleFromState?.price || 0,
    vehicle_year: vehicleFromState?.year || 0,
    vehicle_image: vehicleFromState?.image_url || "",
    is_vehicle_inquiry: !!vehicleFromState
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const subjects = [
    "Allgemeine Anfrage",
    "Fahrzeuganfrage",
    "Werkstatt-Service",
    "Probefahrt vereinbaren",
    "Finanzierung",
    "Sonstiges"
  ];

  const clearVehicle = () => {
    setFormData(prev => ({
      ...prev,
      subject: "",
      message: "",
      vehicle_id: "",
      vehicle_brand: "",
      vehicle_model: "",
      vehicle_price: 0,
      vehicle_year: 0,
      vehicle_image: "",
      is_vehicle_inquiry: false
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success("Nachricht erfolgreich gesendet!");
    } catch (error) {
      console.error("Error submitting contact:", error);
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-green-100 flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-black  mb-4">
            Vielen Dank für Ihre Nachricht!
          </h1>
          <p className="text-[#888B8D] mb-8">
            Wir haben Ihre Anfrage erhalten und werden uns schnellstmöglich bei Ihnen melden.
          </p>
          <Button 
            onClick={() => {
              setSuccess(false);
              setFormData({ 
                name: "", email: "", phone: "", subject: "", message: "",
                vehicle_id: "", vehicle_brand: "", vehicle_model: "", 
                vehicle_price: 0, vehicle_year: 0, vehicle_image: "", is_vehicle_inquiry: false 
              });
            }}
            className="bg-[#000000] text-white hover:bg-[#1a1a1a"
            data-testid="send-another-btn"
          >
            Weitere Nachricht senden
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-[#000000] py-16" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white  normal-case tracking-tight mb-4">
            Kontakt
          </h1>
          <div className="h-1 w-16 bg-[#EFDF00] mb-6" />
          <p className="text-[#888B8D] max-w-2xl">
            Haben Sie Fragen oder wünschen eine Beratung? Kontaktieren Sie uns – 
            wir sind gerne für Sie da!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h3 className="text-xl font-bold text-black  mb-6">
                  So erreichen Sie uns
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#EFDF00]  flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">Adresse</h4>
                      <p className="text-[#888B8D]">
                        {site.company_street}<br />
                        {site.company_city}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#EFDF00]  flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">Telefon</h4>
                      <a href={telHref(site.company_phone)} className="text-[#888B8D] hover:text-black" data-testid="contact-phone">
                        {site.company_phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#EFDF00]  flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">E-Mail</h4>
                      <a href={`mailto:${site.company_email}`} className="text-[#888B8D] hover:text-black" data-testid="contact-email">
                        {site.company_email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#EFDF00]  flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">Öffnungszeiten</h4>
                      <div className="text-[#888B8D] text-sm space-y-1" data-testid="contact-hours">
                        {hoursLines(site).map((l) => <p key={l}>{l}</p>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className=" h-64 overflow-hidden shadow-none">
                <iframe
                  src="https://maps.google.com/maps?q=Wildenreuth+A+55,+92681+Erbendorf,+Germany&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Auto Thurm Standort"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="lg:col-span-2 border-0 shadow-none">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-black  mb-6">
                  Schreiben Sie uns
                </h3>
                
                {/* Vehicle Inquiry Preview */}
                {formData.is_vehicle_inquiry && formData.vehicle_brand && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-[#000000] to-[#1a1a1a] text-white" data-testid="vehicle-inquiry-preview">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Car className="w-5 h-5 text-[#EFDF00]" />
                        <span className="font-semibold text-[#EFDF00]">Fahrzeuganfrage</span>
                      </div>
                      <button 
                        type="button" 
                        onClick={clearVehicle}
                        className="p-1 hover:bg-white/20 transition-colors"
                        title="Fahrzeug entfernen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex gap-4">
                      {formData.vehicle_image && (
                        <img 
                          src={formData.vehicle_image} 
                          alt={`${formData.vehicle_brand} ${formData.vehicle_model}`}
                          className="w-24 h-16 object-cover"
                        />
                      )}
                      <div>
                        <p className="font-bold text-lg text-white">{formData.vehicle_brand} {formData.vehicle_model}</p>
                        <p className="text-[#D9D9D6] text-sm">
                          Baujahr {formData.vehicle_year} • {formData.vehicle_price?.toLocaleString('de-DE')} €
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-[#333333">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Max Mustermann"
                        className="mt-1"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#333333">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="max@beispiel.de"
                        className="mt-1"
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-[#333333">Telefon</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+49 123 456789"
                        className="mt-1"
                        data-testid="contact-input-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-[#333333">Betreff *</Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                      >
                        <SelectTrigger className="mt-1" data-testid="contact-subject-select">
                          <SelectValue placeholder="Bitte wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {subjects.map((subject) => (
                            <SelectItem key={subject} value={subject}>
                              {subject}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#333333">Nachricht *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Wie können wir Ihnen helfen?"
                      rows={6}
                      className="mt-1"
                      data-testid="contact-input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !formData.subject}
                    className="w-full bg-[#000000] text-white hover:bg-[#1a1a1a font-semibold py-6"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      "Wird gesendet..."
                    ) : (
                      <>
                        <Send className="mr-2 w-5 h-5" />
                        Nachricht senden
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-16 bg-white" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-lg font-bold text-black  mb-8">
            Ihr offizieller Partner für
          </h3>
          <div className="flex justify-center items-center gap-16 flex-wrap">
            <div className="text-center">
              <div className="w-32 h-20 bg-[#EBEBEB]  flex items-center justify-center mb-2">
                <span className="text-2xl font-bold text-black ">RENAULT</span>
              </div>
            </div>
            <div className="text-center">
              <div className="w-32 h-20 bg-[#646B52]  flex items-center justify-center mb-2">
                <span className="text-2xl font-bold text-white ">DACIA</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
